import React, { useEffect, useRef } from "react";
import { Steps } from "primereact/steps";
import { FileUpload } from "primereact/fileupload";
import selectedStepIcon from "../../../assets/icon/selected-step.png";
import { Link } from "react-router-dom";
import PrimaryButton, {
  OutlinedButton,
} from "../../../shared/components/CustomButtons";
import {
  CustomCheckbox,
  CustomDropDown,
  CustomInput,
} from "../../../shared/components/AllInputs";
import { CardBox } from "../../../shared/components/CustomCard";
import { getImageUrl } from "../../../utils/commonFunctions";
import OrderContainer from "./OrderContainer";
import { Tooltip } from "primereact/tooltip";

const PlaceOrder = () => {
  const {
    data,
    setData,
    isLoading,
    furnishingStyles,
    roomList,
    serviceDetails,
    hoveredStyle,
    setHoveredStyle,
    totalPrice,
    handleChange,
    handleAdditionalServicesChange,
    handleFileUpload,
    selectFurninshingStyle,
    calculateTotalPrice,
    onProceedToPay,
    activeIndex,
    setActiveIndex,
    removeImage,
    basicFileUploadRef,
    fileUploadRef,
  } = OrderContainer();

  // Ref for sections
  const sectionsRef = useRef([]);

  const steps = [
    {
      label: "Project Info",
      template: (item) => stepItemRenderer(item, data?.projectName),
    },
    {
      label: "Select Style",
      template: (item) => stepItemRenderer(item, data?.style),
    },
    {
      label: "Upload Photos",
      template: (item) => stepItemRenderer(item, data?.images?.length),
    },
    {
      label: "Photo Details",
      template: (item) =>
        stepItemRenderer(
          item,
          data?.images.length &&
          (data?.images[0]?.items?.length ||
            data?.images[0]?.additionalServices?.length)
        ),
    },
  ];

  const stepItemRenderer = (item, isActive) => {
    return (
      <div className="flex flex-column align-items-center">
        <span
          className={`${isActive ? "border-900 " : "border-300 "
            } inline-flex align-items-center justify-content-center align-items-center border-circle border-1 h-3rem w-3rem z-1 cursor-pointer bg-white`}
        >
          {isActive ? <img src={selectedStepIcon} alt="" /> : null}
        </span>
        <span className="text-left">{item.label}</span>
      </div>
    );
  };

  const labelTemplate = (
    <div className="flex gap-2 items-center text-gray-color cursor-pointer">
      I agree to the
      <span
        className="text-black-alpha-90 hover:underline cursor-pointer"
        onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}
      >
        terms and conditions.
      </span>
    </div>
  );

  // Scroll listener to detect active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      sectionsRef.current.forEach((section, index) => {
        const offsetTop = section.offsetTop;
        const offsetBottom = offsetTop + section.offsetHeight;

        if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
          setActiveIndex(index);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [setActiveIndex]);

  useEffect(() => {
    calculateTotalPrice();
  }, [calculateTotalPrice, data]);

  return (
    <div className="container">
      <h2>Checkout</h2>
      <div className="p-grid p-justify-center">
        <div className="p-col-12 p-md-8">
          {/* Steps for navigation */}
          <Steps
            model={steps}
            activeIndex={activeIndex}
            onSelect={(e) => setActiveIndex(e.index)}
            readOnly={false}
          />

          <div className="p-mt-4">
            {/* Step 1: Project Information */}
            <div className="grid mt-4">
              <div className="lg:col-7">
                <CardBox extraClass="mb-3">
                  <h3>Project </h3>
                  <CustomInput
                    label="Project name"
                    name="projectName"
                    data={data}
                    col={12}
                    onChange={handleChange}
                  />
                </CardBox>
                <CardBox extraClass="mb-3">
                  <h3>Pick a furnishing style for your property</h3>
                  <div className="grid">
                    {furnishingStyles?.map((style, index) => (
                      <div
                        className="col-12 md:col-4 style-card-container"
                        key={index}
                        onMouseEnter={() => setHoveredStyle(style._id)}
                        onMouseLeave={() => setHoveredStyle(null)}
                        onClick={() => selectFurninshingStyle(style._id)}
                      >
                        <CardBox
                          extraClass={`style-card p-0 ${data.style === style._id ||
                            hoveredStyle === style._id
                            ? "hovered"
                            : ""
                            }`}
                          style={{
                            backgroundImage: `url(${getImageUrl(
                              style?.singleImage
                            )})`,
                          }}
                        >
                          <h3 className="text-white title m-0">{style.name}</h3>
                          <p className="text-white title m-0 text-xs">{style?.paragraph}</p>
                          <div className="style-content">
                            <h3 className="m-0">{style.name}</h3>
                            <Link className="text-white" to="">
                              View Example
                            </Link>
                          </div>
                        </CardBox>
                      </div>
                    ))}
                  </div>
                </CardBox>
                <CardBox extraClass="mb-3">
                  <h3>Upload some photos of your property </h3>
                  <div className="file-upload-container">
                    <FileUpload
                      name="file"
                      ref={fileUploadRef}
                      // key={`file-upload-${data.images.length}`}
                      accept=".jpg,.jpeg,.png"
                      maxFileSize={1000000} // 1MB limit
                      headerClassName="hidden"
                      auto={false}
                      emptyTemplate={
                        <>
                          <div className="file-upload-empty-template">
                            <i className="pi pi-cloud-upload file-upload-icon" />
                            <p>Select a file or drag and drop here</p>
                            <small>
                              (allowed file types: .jpg, .jpeg, .png)
                            </small>
                          </div>
                          <FileUpload
                            ref={basicFileUploadRef}
                            mode="basic"
                            name="demo[]"
                            className="mt-3 border-round-lg"
                            accept="image/*"
                            onSelect={(e) => handleFileUpload(e.files[0])}
                            chooseLabel="Select file"
                          />
                        </>
                      }
                    />
                  </div>
                  {data?.formErrors?.["images"] ? (
                    <small className="p-error">
                      {data?.formErrors?.["images"]}
                    </small>
                  ) : null}
                </CardBox>
                {data?.images?.map((image, index) => (
                  <div key={index}>
                    <CardBox extraClass="mb-3">
                      <div className="flex align-items-center justify-content-between mb-3">
                        <h3 className="m-0">Add details about your photos</h3>
                        <div
                          className="bg-red-600 border-circle flex justify-content-center align-items-center"
                          style={{ width: "29px", height: "29px" }}
                          onClick={() => removeImage(index)}
                        >
                          <i className="pi pi-times text-sm text-white"></i>
                        </div>
                      </div>
                      <img
                        src={getImageUrl(image?.beforeImage)}
                        className="w-full border-round-xl mb-4"
                        alt=""
                        style={{
                          width: "278px",
                          height: "278px",
                          objectFit: "cover",
                        }}
                      />
                      <CustomDropDown
                        label="Room/ Area"
                        col={12}
                        options={roomList}
                        data={image}
                        placeholder="Select"
                        name="area"
                        formArrayName="images"
                        index={index}
                        onChange={handleChange}
                      />
                      {/* <p>Would you like any of these items in your room?</p> */}
                      <div className="lg:w-9">
                        {serviceDetails?.items?.map((option, itemIndex) => (
                          <div
                            key={itemIndex}
                            className="flex justify-content-between gap-2 align-items-center mb-2"
                          >
                            <CustomCheckbox
                              label={option.name}
                              name={"items"}
                              formArrayName="images"
                              checked={image?.items?.some(
                                (item) => item.name === option.name
                              )}
                              onChange={(e) =>
                                handleAdditionalServicesChange({
                                  name: e.name,
                                  value: {
                                    name: option.name,
                                    price: option.price,
                                  },
                                  formArrayName: e.formArrayName,
                                  index,
                                })
                              }
                            />
                            <div className="flex gap-3">
                              <span className="text-base">
                                ${option.price} extra
                              </span>
                              <i className="pi pi-info-circle"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* <CustomInput
                        col={12}
                        label="Not mentioned? Type them in here"
                        name={"items"}
                        formArrayName="images"
                      // onChange={(e) =>
                      //   handleAdditionalServicesInput({
                      //     name: e.name,
                      //     checked: e.checked,
                      //     value: {
                      //       name: option.name,
                      //       price: option.price,
                      //     },
                      //     formArrayName: e.formArrayName,
                      //     index,
                      //   })
                      // }
                      /> */}
                    </CardBox>
                    {serviceDetails?.additionalServices &&
                      serviceDetails?.additionalServices.length ? (
                      <CardBox extraClass="mb-3">
                        <h3>Additional service</h3>
                        <div className="lg:w-9">
                          {serviceDetails?.additionalServices?.map(
                            (option, itemIndex) => (
                              <div
                                key={itemIndex}
                                className="flex justify-content-between gap-2 align-items-center mb-2"
                              >
                                <CustomCheckbox
                                  label={option.name}
                                  name={"additionalServices"}
                                  formArrayName="images"
                                  checked={image?.additionalServices?.some(
                                    (item) => item.name === option.name
                                  )}
                                  onChange={(e) =>
                                    handleAdditionalServicesChange({
                                      name: e.name,
                                      value: {
                                        name: option.name,
                                        price: option.price,
                                      },
                                      formArrayName: e.formArrayName,
                                      index,
                                    })
                                  }
                                />
                                <div className="flex gap-3">
                                  <span className="text-base">
                                    ${option.price} extra
                                  </span>
                                  <i className="pi pi-info-circle"></i>
                                  <Tooltip target=".pi-info-circle">
                                    Lorem ipsum, or lipsum as it is sometimes
                                    known, is dummy text used in laying out
                                    print, graphic or web designs.
                                  </Tooltip>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </CardBox>
                    ) : null}
                  </div>
                ))}
                <CustomCheckbox
                  extraClassName="flex align-items-center"
                  col={12}
                  name="agreeToTermsAndConditions"
                  checked={data.agreeToTermsAndConditions}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      agreeToTermsAndConditions: e.checked,
                    }))
                  }
                  label={labelTemplate}
                />
              </div>
              <div className="col-12 lg:col-5">
                <CardBox extraClass="mb-3">
                  <h3>Order Summary</h3>
                  <div
                    className={`flex justify-content-between border-400 py-3 ${data?.images.length ? "border-bottom-1" : ""
                      }`}
                  >
                    <span className="text-sm text-gray-color">
                      {serviceDetails?.serviceName} for : {data?.images.length}{" "}
                      photos
                    </span>
                    <span className="font-semibold">
                      Total: $
                      {serviceDetails?.price
                        ? serviceDetails?.price * data?.images.length
                        : 0}
                    </span>
                  </div>
                  <div
                    className={`border-400 py-3 ${data?.images.length ? "border-bottom-1" : ""
                      }`}
                  >
                    {data?.images[0]?.items?.map((serv, index) => {
                      return (
                        <div
                          key={index}
                          className="flex justify-content-between"
                        >
                          <span className="text-sm text-gray-color">
                            {serv?.name} for {data?.images?.length} photos
                          </span>
                          <span className="font-semibold">
                            ${(data?.images?.length * serv?.price).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                    {data?.images[0]?.additionalServices?.map((serv, index) => {
                      return (
                        <div
                          key={index}
                          className="flex justify-content-between"
                        >
                          <span className="text-sm text-gray-color">
                            {serv?.name} for {data.images.length} photos
                          </span>
                          <span className="font-semibold">
                            ${(data?.images.length * serv?.price).toFixed(2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-content-between border-400 py-3">
                    <span className="text-sm text-gray-color">Total</span>
                    <span className="font-semibold">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                </CardBox>
                <PrimaryButton
                  disabled={!totalPrice}
                  label="Proceed to Pay"
                  extraClassNames="mb-3"
                  onClick={() => onProceedToPay(false)}
                />
                <OutlinedButton
                  disabled={!totalPrice}
                  label="Save as Draft"
                  onClick={() => onProceedToPay(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceOrder;
